import { useState, useMemo } from 'react';
import { computeTradeSuggestions, loadSuggestionsSnooze, snoozeSuggestion } from '../utils/tradeSuggestions';

/**
 * Banner proactivo en el Resumen: muestra los 3 mejores matches con
 * amigos para que el usuario abra el trato directo sin pasar por
 * Mercado Global → filtrar → encontrar → abrir cada uno.
 *
 * Cada card incluye:
 *   - Nombre + score del match (R ⇄ G).
 *   - Vista previa de hasta 4 láminas de cada lado.
 *   - [Ver trato] → onVisitFriend(uid).
 *   - [Saltar 24h] → snooze localStorage.
 *
 * Si no hay sugerencias, no renderiza nada.
 */
export default function TradeSuggestionsBanner({
  friendsList,
  trades,
  userUid,
  onVisitFriend,
}) {
  const [snoozeBump, setSnoozeBump] = useState(0);
  const [collapsed, setCollapsed] = useState(false);

  const suggestions = useMemo(() => {
    if (!userUid) return [];
    const dismissedMap = loadSuggestionsSnooze(userUid);
    return computeTradeSuggestions({ friendsList, trades, dismissedMap });
    // snoozeBump dispara recálculo al esnoozar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friendsList, trades, userUid, snoozeBump]);

  if (suggestions.length === 0) return null;

  const handleSnooze = (friendUid) => {
    snoozeSuggestion(userUid, friendUid);
    setSnoozeBump(v => v + 1);
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(212, 168, 67, 0.12), rgba(212, 168, 67, 0.04))',
      border: '1px solid rgba(212, 168, 67, 0.35)',
      borderRadius: 14,
      padding: '14px 16px',
      marginBottom: 20,
      animation: 'viewFadeIn 0.3s ease',
    }}>
      <div
        onClick={() => setCollapsed(v => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCollapsed(v => !v); } }}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: collapsed ? 0 : 12,
          color: 'var(--accent-gold)',
          fontWeight: 700,
          fontSize: '0.95rem',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: '1.2rem' }}>💡</span>
        <span>
          {suggestions.length === 1
            ? 'Tienes 1 trato sugerido'
            : `Tienes ${suggestions.length} tratos sugeridos`}
        </span>
        <span style={{
          marginLeft: 'auto',
          fontSize: '0.72rem',
          color: 'var(--text-muted)',
          fontWeight: 500,
          transition: 'transform 0.2s',
          transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)',
        }}>▼</span>
      </div>

      {!collapsed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {suggestions.map(f => {
            const score = f.stats?.match?.score || 0;
            const receive = f.stats?.match?.receive || [];
            const give = f.stats?.match?.give || [];
            const previewMax = 4;
            const receivePreview = receive.slice(0, previewMax);
            const givePreview = give.slice(0, previewMax);
            const receiveMore = Math.max(0, receive.length - previewMax);
            const giveMore = Math.max(0, give.length - previewMax);

            return (
              <div
                key={f.uid}
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  borderRadius: 10,
                  padding: '10px 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  flexWrap: 'wrap',
                }}>
                  <strong style={{ color: 'var(--text-primary)', fontSize: '0.92rem' }}>
                    {f.name}
                  </strong>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    Match{' '}
                    <span style={{ color: '#4ade80', fontWeight: 600 }}>{receive.length}</span>
                    {' ⇄ '}
                    <span style={{ color: '#60a5fa', fontWeight: 600 }}>{give.length}</span>
                  </span>
                </div>

                {/* Preview de láminas */}
                {receivePreview.length > 0 && (
                  <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    <span style={{ color: '#4ade80', fontWeight: 600 }}>Recibirías:</span>{' '}
                    {receivePreview.map((n, i) => (
                      <span key={n} style={{
                        fontSize: '0.7rem',
                        background: 'rgba(74, 222, 128, 0.15)',
                        color: '#4ade80',
                        padding: '1px 6px',
                        borderRadius: 5,
                        marginRight: 3,
                      }}>{n}</span>
                    ))}
                    {receiveMore > 0 && <span> +{receiveMore} más</span>}
                  </div>
                )}
                {givePreview.length > 0 && (
                  <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    <span style={{ color: '#60a5fa', fontWeight: 600 }}>Entregarías:</span>{' '}
                    {givePreview.map((n) => (
                      <span key={n} style={{
                        fontSize: '0.7rem',
                        background: 'rgba(96, 165, 250, 0.15)',
                        color: '#60a5fa',
                        padding: '1px 6px',
                        borderRadius: 5,
                        marginRight: 3,
                      }}>{n}</span>
                    ))}
                    {giveMore > 0 && <span> +{giveMore} más</span>}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                  <button
                    className="btn btn-gold"
                    style={{ padding: '6px 14px', fontSize: '0.78rem', fontWeight: 700 }}
                    onClick={() => onVisitFriend?.(f.uid)}
                    aria-label={`Abrir trato con ${f.name}`}
                  >
                    🤝 Ver trato
                  </button>
                  <button
                    className="btn"
                    style={{
                      padding: '6px 12px',
                      fontSize: '0.78rem',
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: 'var(--text-secondary)',
                      border: '1px solid var(--border-color)',
                    }}
                    onClick={() => handleSnooze(f.uid)}
                    aria-label={`Saltar sugerencia con ${f.name}`}
                    title="No mostrarme esta sugerencia en 24h"
                  >
                    Saltar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
